"use client";

import { Bell } from "lucide-react";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import { projectDetailPath } from "@/lib/aicheck/routes";
import { authService } from "@/services/auth";

interface NotificationItem {
  notificationId: string;
  projectKey: string;
  kind: string;
  title: string;
  body: string;
  createdAt: string;
  readAt: string | null;
  /** ③人間語の補足の一言（参考・任意）。未生成なら null/undefined。 */
  gist?: string | null;
}

const POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Inbox bell: unread badge + dropdown of recent server-side notifications. */
export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await authService.getIdToken();
      if (!token) return;
      const res = await fetch(apiUrl("/api/aicheck/notifications"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as {
        notifications?: NotificationItem[];
        unreadCount?: number;
      };
      setItems(data.notifications ?? []);
      setUnread(data.unreadCount ?? 0);
    } catch {
      // 通知は補助情報 — 取得失敗は無視
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const markAllRead = useCallback(async () => {
    try {
      const token = await authService.getIdToken();
      if (!token) return;
      await fetch(apiUrl("/api/aicheck/notifications"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnread(0);
      setItems((current) =>
        current.map((item) => ({
          ...item,
          readAt: item.readAt ?? new Date().toISOString(),
        })),
      );
    } catch {
      // ignore
    }
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="通知"
        className="relative inline-flex size-7 items-center justify-center rounded border border-zinc-700 text-zinc-400 transition-colors hover:border-zinc-600 hover:text-white"
      >
        <Bell size={14} />
        {unread > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium leading-none text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-96 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-2">
            <span className="text-sm font-semibold text-zinc-800">通知</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-xs text-indigo-600 hover:underline"
              >
                すべて既読にする
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-zinc-400">
                通知はありません
              </p>
            ) : (
              items.map((item) => (
                <Link
                  key={item.notificationId}
                  href={projectDetailPath(item.projectKey)}
                  onClick={() => setOpen(false)}
                  className={`block border-b border-zinc-50 px-3 py-2.5 last:border-b-0 hover:bg-zinc-50 ${
                    item.readAt ? "opacity-60" : ""
                  }`}
                >
                  <p className="text-xs font-medium text-zinc-800">
                    {item.title}
                  </p>
                  {item.body && (
                    <p className="mt-0.5 line-clamp-2 text-[11px] text-zinc-500">
                      {item.body}
                    </p>
                  )}
                  {item.gist && (
                    <p className="mt-0.5 line-clamp-2 text-[11px] italic text-indigo-500">
                      {item.gist}
                    </p>
                  )}
                  <p className="mt-0.5 text-[10px] text-zinc-400">
                    {new Date(item.createdAt).toLocaleString("ja-JP")}
                  </p>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
